// What the guide for a face alignment shows (CO-5851): the faces being
// aligned on both products, filled and outlined, and one fainter translucent
// bridge in the plane they share, connecting every face lined up in it, with
// no outline of its own.
// A wall's faces are kept apart, to be drawn behind what stands in front of
// them. DragGuides draws them in MOVEMENT_GUIDE_COLOUR.
import * as THREE from 'three';
import {
    Axis,
    axisIndex,
    axisVector,
} from 'components/customer/RoomPlanner/helpers/axes';
import {
    flattenBox,
    thinnestAxis,
} from 'components/customer/RoomPlanner/helpers/boxes';
import {
    AlignmentBody,
    HeldFaceAlignment,
} from 'components/customer/RoomPlanner/helpers/movement/faceAlignment';
import type {SnapPlane} from 'components/customer/RoomPlanner/helpers/snapping/strategies/faceSnap';
import {outlineOfFaces} from 'components/customer/RoomPlanner/helpers/movement/planeFaces';
import {WallFacts} from 'components/customer/RoomPlanner/helpers/snapping/roomWalls';

// The bridge as faint as the axis guide's planes; the faces stronger, so
// they read as the things lined up.
export const ALIGNMENT_BRIDGE_OPACITY = 0.18;
export const ALIGNED_FACE_OPACITY = 0.35;

// A plane seen within about 12 degrees of edge-on, as in plan and elevation
// views, which look straight along one axis: its fill collapses to nothing
// there, so it is drawn as its outline instead.
const EDGE_ON = 0.2;

// Faces closer to the shared plane than this (0.05 mm) lie in it.
const IN_PLANE = 0.01;

/**
 * The rectangles the guide draws, each a box flat on the aligned axis at the
 * shared plane.
 */
export interface AlignmentGuide {
    // The other product's faces in the plane.
    targetFaces: THREE.Box3[];
    // The dragged product's faces in the plane.
    movingFaces: THREE.Box3[];
    // Other products' faces in the plane, in line with these (CO-5858),
    // highlighted with them.
    planeFaces: THREE.Box3[];
    // The walls' faces in the plane: the target faces when it lines up with
    // the walls, and any others in line.
    wallFaces: THREE.Box3[];
    // The smallest rectangle covering every one of them, walls' included,
    // so one plane connects them all.
    bridge: THREE.Box3;
    // The shared plane.
    plane: SnapPlane;
}

// A face's corners, to tell one found twice from two faces.
const keyOf = ({min, max}: THREE.Box3) =>
    [...min.toArray(), ...max.toArray()].map((n) => n.toFixed(2)).join(',');

/**
 * @param {HeldFaceAlignment} alignment The alignment held this move.
 * @param {AlignmentBody} moving The dragged product where it landed.
 * @param {Function} [roomProductFaces] Every other product's faces in a plane
 * (PlaneFaces), when the room's faces in line are to show too.
 * @param {Function} [roomWallFaces] The walls' faces in a plane, likewise.
 * @return {AlignmentGuide} What to draw.
 */
export const alignmentGuide = (
    alignment: HeldFaceAlignment,
    moving: AlignmentBody,
    roomProductFaces?: (plane: SnapPlane) => THREE.Box3[],
    roomWallFaces?: (plane: SnapPlane) => THREE.Box3[]
): AlignmentGuide => {
    const {other, axis, position} = alignment;
    const index = axisIndex(axis);
    const inPlane = (at: number) => Math.abs(at - position) < IN_PLANE;
    const facesInPlane = (
        faces: AlignmentBody['faces'],
        bbox: THREE.Box3
    ): THREE.Box3[] => {
        const found = faces
            .filter((face) => face.axis === axis && inPlane(face.position))
            .map((face) => face.rect.clone());

        return found.length ? found : [flattenBox(bbox, index, position)];
    };

    // On the floor axes, the exterior faces in the plane. On height, the
    // tops or bottoms of the other's solid components there, or its own
    // footprint when the plane is the bottom of its legs.
    const targetFaces =
        axis === 'y'
            ? other.solidBBoxes
                  .filter((bbox) => inPlane(bbox.min.y) || inPlane(bbox.max.y))
                  .map((bbox) => flattenBox(bbox, index, position))
            : facesInPlane(other.exteriorFaces, other.bbox);
    if (targetFaces.length === 0) {
        targetFaces.push(flattenBox(other.bbox, index, position));
    }
    const movingFaces =
        axis === 'y'
            ? [flattenBox(moving.bbox, index, position)]
            : facesInPlane(moving.faces, moving.bbox);

    const shown = new Set([...targetFaces, ...movingFaces].map(keyOf));
    const inLine = (faces?: (plane: SnapPlane) => THREE.Box3[]) =>
        (faces?.({axis, position}) ?? []).filter(
            (face) => !shown.has(keyOf(face))
        );

    const planeFaces = inLine(roomProductFaces);
    const wallsInLine = inLine(roomWallFaces);
    const bridge = [
        ...targetFaces,
        ...movingFaces,
        ...planeFaces,
        ...wallsInLine,
    ].reduce((union, face) => union.union(face), new THREE.Box3());

    return {
        targetFaces,
        movingFaces,
        planeFaces,
        wallFaces: [
            ...(other instanceof WallFacts ? targetFaces : []),
            ...wallsInLine,
        ],
        bridge,
        plane: {axis, position},
    };
};

/**
 * @param {THREE.Box3} rect A box flat on one axis.
 * @return {THREE.Vector3[]} Its four corners, in order around it.
 */
const cornersOf = (rect: THREE.Box3): THREE.Vector3[] => {
    const flat = thinnestAxis(rect);
    const [u, v] = [0, 1, 2].filter((i) => i !== flat);
    const corner = (useMaxU: boolean, useMaxV: boolean) => {
        const point = rect.min.clone();
        if (useMaxU) point.setComponent(u, rect.max.getComponent(u));
        if (useMaxV) point.setComponent(v, rect.max.getComponent(v));

        return point;
    };

    return [
        corner(false, false),
        corner(true, false),
        corner(true, true),
        corner(false, true),
    ];
};

/**
 * @param {THREE.Box3[]} rects Boxes flat on one axis.
 * @param {number} alpha Their opacity.
 * @return {object} The rectangles as triangles: each vertex's position,
 * three numbers apiece, and its opacity.
 */
const fill = (
    rects: THREE.Box3[],
    alpha: number
): {positions: number[]; alphas: number[]} => {
    const positions: number[] = [];
    const alphas: number[] = [];
    rects.forEach((rect) => {
        const [a, b, c, d] = cornersOf(rect);
        [a, b, c, a, c, d].forEach((point) => {
            positions.push(point.x, point.y, point.z);
            alphas.push(alpha);
        });
    });

    return {positions, alphas};
};

/**
 * @param {THREE.Box3[]} rects Boxes flat on one axis.
 * @return {Array<THREE.Vector3[]>} Their sides, as pairs of ends.
 */
const outline = (rects: THREE.Box3[]): THREE.Vector3[][] =>
    rects.flatMap((rect) => {
        const corners = cornersOf(rect);

        return corners.map((corner, i) => [
            corner,
            corners[(i + 1) % corners.length],
        ]);
    });

/**
 * @param {AlignmentGuide} guide What to draw.
 * @return {object} The bridge's fill, in the axis guide's style.
 */
export const alignmentBridgePlanes = (guide: AlignmentGuide) =>
    fill([guide.bridge], ALIGNMENT_BRIDGE_OPACITY);

/**
 * @param {AlignmentGuide} guide What to draw.
 * @return {Array<THREE.Vector3[]>} The bridge's sides, drawn only when it is
 * seen edge-on (isEdgeOn).
 */
export const alignmentBridgeOutline = (guide: AlignmentGuide) =>
    outline([guide.bridge]);

/**
 * @param {Axis} axis The axis the shared plane lies across.
 * @param {THREE.Vector3} viewDirection Which way the camera looks.
 * @return {boolean} True when the plane is seen edge-on, or close to it.
 */
export const isEdgeOn = (axis: Axis, viewDirection: THREE.Vector3): boolean =>
    Math.abs(axisVector(axis).dot(viewDirection.clone().normalize())) < EDGE_ON;

/**
 * @param {AlignmentGuide} guide What to draw.
 * @return {THREE.Box3[]} Every product's face lined up: both products' and
 * any others in the plane, the walls' left out.
 */
const productFaces = (guide: AlignmentGuide) => [
    ...guide.targetFaces.filter((face) => !guide.wallFaces.includes(face)),
    ...guide.movingFaces,
    ...guide.planeFaces,
];

/**
 * @param {AlignmentGuide} guide What to draw.
 * @return {object} The fill of every product's face lined up.
 */
export const alignedFacePlanes = (guide: AlignmentGuide) =>
    fill(productFaces(guide), ALIGNED_FACE_OPACITY);

/**
 * @param {AlignmentGuide} guide What to draw.
 * @return {Array<THREE.Vector3[]>} One outline round every product's face
 * lined up together, other products' in the plane included
 * (outlineOfFaces), with no line where two meet or overlap.
 */
export const alignedFaceOutline = (guide: AlignmentGuide) =>
    outlineOfFaces(productFaces(guide), guide.plane);

/**
 * @param {AlignmentGuide} guide What to draw.
 * @return {object} The fill of the walls' faces lined up.
 */
export const alignedWallPlanes = (guide: AlignmentGuide) =>
    fill(guide.wallFaces, ALIGNED_FACE_OPACITY);

/**
 * @param {AlignmentGuide} guide What to draw.
 * @return {Array<THREE.Vector3[]>} One outline round the walls' faces lined
 * up together.
 */
export const alignedWallOutline = (guide: AlignmentGuide) =>
    outlineOfFaces(guide.wallFaces, guide.plane);
